import { orderBy } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { PaginationMetadata, UsePagination } from '~/react-hooks/usePagination';
import InfiniteScroll from 'react-infinite-scroller';
import LibraryItems from './Placeholders/LibraryItems';
import { MediaLibraryAsset } from './InfiniteMediaLibraryItem';
import cx from 'classnames';
import InfiniteMediaLibraryItems from './InfiniteMediaLibraryItems';

import InfinityMediaItemConfigContext, {
  defaultItemConfig,
  InfiniteMediaItemConfigProps,
} from '../Context/infinite-media-item-config-context';

export enum GridTileType {
  Dynamic = 'dynamic',
  Static = 'static',
}

export interface InfiniteMediaLibraryProps {
  paginationData: UsePagination<MediaLibraryAsset, PaginationMetadata>[];
  perPage: number;
  onTagUpdated?: (resourceId?: string) => void;
  emptyComponent?: React.ReactNode;
  scrollContainerRef?: React.RefObject<HTMLElement>;
  gridTileType?: GridTileType;
  itemConfig?: InfiniteMediaItemConfigProps;
  className?: string;
  orderByCreatedAt?: boolean;
  selectedIds?: string[];
  addSelectedId?: (id: string) => void;
  removeSelectedId?: (id: string) => void;
  isItemSelectDisabled?: (id: string) => boolean;
}

const InfiniteMediaLibrary = ({
  paginationData,
  perPage,
  onTagUpdated,
  emptyComponent,
  scrollContainerRef,
  gridTileType = GridTileType.Static,
  itemConfig = defaultItemConfig,
  className,
  orderByCreatedAt = true,
  addSelectedId,
  selectedIds,
  removeSelectedId,
  isItemSelectDisabled,
}: InfiniteMediaLibraryProps) => {
  const isDynamicGridTileType = gridTileType === GridTileType.Dynamic;
  const [currentPage, setCurrentPage] = useState(1);

  const { canLoadMore, isLoading, isFetching, emptyList } = useMemo(() => {
    let canLoadMore = false;
    let isLoading = false;
    let isFetching = false;
    const currentPage = 1;
    let total = 0;

    paginationData.forEach((data) => {
      const metadata = data[2];

      if (metadata.loading) {
        isLoading = true;
      }

      if (metadata.isFetching) {
        isFetching = true;
      }

      if (metadata.total) {
        total += metadata.total;
      }
    });
    canLoadMore = Math.sign(total - currentPage * perPage) === 1;

    return {
      canLoadMore,
      isLoading,
      isFetching,
      currentPage,
      emptyList: !isLoading && !isFetching && total === 0,
    };
  }, [paginationData]);

  const loadMore = useCallback(() => {
    if (canLoadMore) {
      setCurrentPage(currentPage + 1);

      paginationData.forEach((data) => {
        const [, paginator, metadata] = data;

        if (metadata.canFetchMore) {
          void paginator.more();
        }
      });
    }
  }, [canLoadMore, paginationData]);

  const libraryItems = useMemo<MediaLibraryAsset[]>(() => {
    /**
     * Extract the results from the paginationData and combine them into a single array
     *
     * Then we slice the data starting from 0 until (page * perPage) since the data on this range is properly sorted.
     *
     * The excess data after (page * perPage) is not guaranteed to be sorted properly since we are getting twice as much
     * data and merging them together. We don't know if the next page has some items that can be inserted
     * in the middle of the excess data.
     *
     * Solution is patterned from http://mysql.rjweb.org/doc.php/pagination#pagination_and_union
     */
    const results = paginationData.flatMap<MediaLibraryAsset>(
      (data) => data[0]
    );

    if (orderByCreatedAt) {
      return orderBy(results, ['created_at'], 'desc').slice(
        0,
        perPage * currentPage
      );
    } else {
      return results.slice(0, perPage * currentPage);
    }
  }, [paginationData, orderByCreatedAt, perPage, currentPage]);

  return (
    <div
      className={cx(className, {
        'pb-28': !isDynamicGridTileType,
        'pt-5 pb-10': isDynamicGridTileType,
      })}
    >
      {emptyList ? (
        emptyComponent
      ) : (
        <InfinityMediaItemConfigContext.Provider value={itemConfig}>
          <InfiniteScroll
            loadMore={loadMore}
            hasMore={canLoadMore}
            useWindow={false}
            getScrollParent={() => scrollContainerRef?.current as HTMLElement}
          >
            <div className='flex flex-wrap max-w-full'>
              <InfiniteMediaLibraryItems
                gridTileType={gridTileType}
                libraryItems={libraryItems}
                onTagUpdated={onTagUpdated}
                addSelectedId={addSelectedId}
                selectedIds={selectedIds}
                removeSelectedId={removeSelectedId}
                isItemSelectDisabled={isItemSelectDisabled}
              />
            </div>
          </InfiniteScroll>
        </InfinityMediaItemConfigContext.Provider>
      )}
      {!emptyList && (isLoading || isFetching) && <LibraryItems />}
    </div>
  );
};

export default InfiniteMediaLibrary;
